import React from 'react';
import PropTypes from 'prop-types';
import { Field, TextArea, TextInput } from '../../../../components/form/FormControls';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { normalizePatientId } from '../../../../utils/patientAccess';
import { FIELD_GRID, MissingTag, ReadValue, SectionCard, UnitInput, YesNo, fieldAnchor } from '../VisitUi';

const GRID_3 = 'tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3';
const GRID_4 = 'tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 xl:tw-grid-cols-4';

/**
 * Home visit evaluation, sections A–J (same fields and handlers as before:
 * top-level fields through handleInputChange, visitEvaluation.* through
 * handleEvalChange). H is shown to admins only.
 */
const HomeEvaluation = ({ formData, isReadOnly, isAdmin, missingKeys, sections, onChange, onEvalChange }) => {
  const { t } = useTranslation();
  const f = (key) => t(`visitModal.evaluation.fields.${key}`);
  const ev = formData.visitEvaluation || {};
  const hoursUnit = t('visitModal.common.hours');
  const minutesUnit = t('visitModal.common.minutes');

  const section = (letter) => ({
    letter,
    title: t(`visitModal.evaluation.home.${letter}.title`),
    hint: t(`visitModal.evaluation.home.${letter}.hint`),
    missingCount: sections[letter]?.missing || 0,
    complete: sections[letter]?.total > 0 && !sections[letter]?.missing,
  });
  const req = (key) => ({ required: true, badge: missingKeys.has(key) ? <MissingTag /> : null });

  const dateInput = (key, value, handler) => (
    <TextInput id={fieldAnchor(key)} type="date" value={value || ''} onChange={(event) => handler(event.target.value)} disabled={isReadOnly} />
  );

  return (
    <div className="tw-space-y-4">
      <SectionCard {...section('A')} tone="slate">
        <div className={FIELD_GRID}>
          <Field label={f('patientId')}>
            <ReadValue tone={normalizePatientId(formData.patientId) ? 'neutral' : 'muted'}>{normalizePatientId(formData.patientId) || '-'}</ReadValue>
          </Field>
          <Field label={f('visitId')}>
            <ReadValue tone={formData.visitNumber ? 'neutral' : 'muted'}>{formData.visitNumber || '-'}</ReadValue>
          </Field>
        </div>
      </SectionCard>

      <SectionCard {...section('B')} tone="sky">
        <div className={GRID_3}>
          <Field label={f('windowOpenDate')} htmlFor={fieldAnchor('windowOpenDate')} {...req('windowOpenDate')}>
            {dateInput('windowOpenDate', ev.windowOpenDate, (value) => onEvalChange('windowOpenDate', value))}
          </Field>
          <Field label={f('targetDate')} htmlFor={fieldAnchor('targetDate')} {...req('targetDate')}>
            {dateInput('targetDate', ev.targetDate, (value) => onEvalChange('targetDate', value))}
          </Field>
          <Field label={f('windowCloseDate')} htmlFor={fieldAnchor('windowCloseDate')} {...req('windowCloseDate')}>
            {dateInput('windowCloseDate', ev.windowCloseDate, (value) => onEvalChange('windowCloseDate', value))}
          </Field>
        </div>
      </SectionCard>

      <SectionCard {...section('C')} tone="teal">
        <div className={GRID_3}>
          <Field label={f('clinician')} htmlFor={fieldAnchor('clinicianFullName')} {...req('clinicianFullName')}>
            <TextInput
              id={fieldAnchor('clinicianFullName')}
              type="text"
              value={formData.clinicianFullName || ''}
              onChange={(event) => {
                onChange('clinicianFullName', event.target.value);
                onEvalChange('clinicianFullName', event.target.value);
              }}
              disabled={isReadOnly}
              placeholder={f('clinicianPlaceholder')}
            />
          </Field>
          <Field label={f('plannedDate')} htmlFor="vm-home-plannedDate">
            <TextInput id="vm-home-plannedDate" type="date" value={formData.date || ''} onChange={(event) => onChange('date', event.target.value)} disabled={isReadOnly} />
          </Field>
          <Field label={f('plannedTime')} htmlFor="vm-home-plannedTime">
            <TextInput id="vm-home-plannedTime" type="time" value={formData.startTime || ''} onChange={(event) => onChange('startTime', event.target.value)} disabled={isReadOnly} />
          </Field>
        </div>
      </SectionCard>

      <SectionCard {...section('D')} tone="indigo">
        <div className={GRID_3}>
          <Field label={`${f('travelTime')} (${hoursUnit})`} htmlFor={fieldAnchor('travelBefore')} {...req('travelBefore')}>
            <UnitInput id={fieldAnchor('travelBefore')} unit={hoursUnit} type="number" min="0" placeholder="0" value={ev.travelBeforeHours || ''} onChange={(event) => onEvalChange('travelBeforeHours', event.target.value)} disabled={isReadOnly} />
          </Field>
          <Field label={`${f('travelTime')} (${minutesUnit})`} htmlFor="vm-home-travelBeforeMinutes">
            <UnitInput id="vm-home-travelBeforeMinutes" unit={minutesUnit} type="number" min="0" max="59" placeholder="0" value={ev.travelBeforeMinutes || ''} onChange={(event) => onEvalChange('travelBeforeMinutes', event.target.value)} disabled={isReadOnly} />
          </Field>
          <Field label={f('distance')} htmlFor={fieldAnchor('travelDistanceBefore')} {...req('travelDistanceBefore')}>
            <UnitInput id={fieldAnchor('travelDistanceBefore')} unit={t('visitModal.common.km')} type="number" min="0" step="0.1" placeholder="0" value={ev.travelDistanceBefore || ''} onChange={(event) => onEvalChange('travelDistanceBefore', event.target.value)} disabled={isReadOnly} />
          </Field>
        </div>
      </SectionCard>

      <SectionCard {...section('E')} tone="emerald">
        <div className="tw-space-y-4">
          <div className={GRID_4}>
            <Field label={f('visitStartDate')} htmlFor={fieldAnchor('visitStartDate')} {...req('visitStartDate')}>
              {dateInput('visitStartDate', formData.visitStartDate, (value) => onChange('visitStartDate', value))}
            </Field>
            <Field label={f('visitStartTime')} htmlFor={fieldAnchor('visitStartTime')} {...req('visitStartTime')}>
              <TextInput id={fieldAnchor('visitStartTime')} type="time" value={formData.visitStartTime || ''} onChange={(event) => onChange('visitStartTime', event.target.value)} disabled={isReadOnly} />
            </Field>
            <Field label={f('visitEndDate')} htmlFor={fieldAnchor('visitEndDate')} {...req('visitEndDate')}>
              {dateInput('visitEndDate', formData.visitEndDate, (value) => onChange('visitEndDate', value))}
            </Field>
            <Field label={f('visitEndTime')} htmlFor={fieldAnchor('visitEndTime')} {...req('visitEndTime')}>
              <TextInput id={fieldAnchor('visitEndTime')} type="time" value={formData.visitEndTime || ''} onChange={(event) => onChange('visitEndTime', event.target.value)} disabled={isReadOnly} />
            </Field>
          </div>
          <div className={FIELD_GRID}>
            <Field label={`${f('visitDuration')} (${hoursUnit})`} htmlFor={fieldAnchor('visitDuration')} {...req('visitDuration')}>
              <UnitInput id={fieldAnchor('visitDuration')} unit={hoursUnit} type="number" min="0" placeholder="0" value={ev.visitDurationHours || ''} onChange={(event) => onEvalChange('visitDurationHours', event.target.value)} disabled={isReadOnly} />
            </Field>
            <Field label={`${f('visitDuration')} (${minutesUnit})`} htmlFor="vm-home-visitDurationMinutes">
              <UnitInput id="vm-home-visitDurationMinutes" unit={minutesUnit} type="number" min="0" max="59" placeholder="0" value={ev.visitDurationMinutes || ''} onChange={(event) => onEvalChange('visitDurationMinutes', event.target.value)} disabled={isReadOnly} />
            </Field>
          </div>
        </div>
      </SectionCard>

      <SectionCard {...section('F')} tone="indigo">
        <div className={FIELD_GRID}>
          <Field label={`${f('travelTime')} (${hoursUnit})`} htmlFor={fieldAnchor('travelAfter')} {...req('travelAfter')}>
            <UnitInput id={fieldAnchor('travelAfter')} unit={hoursUnit} type="number" min="0" placeholder="0" value={ev.travelAfterHours || ''} onChange={(event) => onEvalChange('travelAfterHours', event.target.value)} disabled={isReadOnly} />
          </Field>
          <Field label={`${f('travelTime')} (${minutesUnit})`} htmlFor="vm-home-travelAfterMinutes">
            <UnitInput id="vm-home-travelAfterMinutes" unit={minutesUnit} type="number" min="0" max="59" placeholder="0" value={ev.travelAfterMinutes || ''} onChange={(event) => onEvalChange('travelAfterMinutes', event.target.value)} disabled={isReadOnly} />
          </Field>
        </div>
      </SectionCard>

      <SectionCard {...section('G')} tone="rose">
        <div className="tw-space-y-4">
          <div className={FIELD_GRID}>
            <Field label={f('issueOccurred')} {...req('issueOccurred')}>
              <div id={fieldAnchor('issueOccurred')}>
                <YesNo label={f('issueOccurred')} value={ev.issueOccurred} onChange={(value) => onEvalChange('issueOccurred', value)} disabled={isReadOnly} />
              </div>
            </Field>
            <Field label={f('issueResultedInMiss')} required={ev.issueOccurred === 'yes'} badge={missingKeys.has('issueResultedInMiss') ? <MissingTag /> : null}>
              <div id={fieldAnchor('issueResultedInMiss')}>
                <YesNo label={f('issueResultedInMiss')} value={ev.issueResultedInMiss} onChange={(value) => onEvalChange('issueResultedInMiss', value)} disabled={isReadOnly} />
              </div>
            </Field>
          </div>
          <Field label={f('issueSource')} htmlFor={fieldAnchor('issueSource')} required={ev.issueOccurred === 'yes'} badge={missingKeys.has('issueSource') ? <MissingTag /> : null}>
            <TextInput id={fieldAnchor('issueSource')} type="text" value={ev.issueCategory || ''} onChange={(event) => onEvalChange('issueCategory', event.target.value)} disabled={isReadOnly} placeholder={f('issueSourcePlaceholder')} />
          </Field>
        </div>
      </SectionCard>

      {isAdmin && (
        <SectionCard {...section('H')} tone="violet" aside={<span className="tw-hidden tw-shrink-0 tw-rounded-full tw-bg-violet-500/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-violet-700 sm:tw-inline dark:tw-text-violet-300">{t('visitModal.evaluation.adminOnly')}</span>}>
          <div className="tw-space-y-4">
            <div className={FIELD_GRID}>
              <Field label={f('smoCorrectionRequests')} htmlFor="vm-home-smoCorrectionRequests">
                <TextInput id="vm-home-smoCorrectionRequests" type="number" min="0" value={ev.smoCorrectionRequests || ''} onChange={(event) => onEvalChange('smoCorrectionRequests', event.target.value)} disabled={isReadOnly} placeholder={f('smoCorrectionRequestsPlaceholder')} />
              </Field>
              <Field label={f('qualityManagerCheck')} htmlFor="vm-home-qualityManagerCheck">
                <TextInput id="vm-home-qualityManagerCheck" type="text" value={ev.qualityManagerCheck || ''} onChange={(event) => onEvalChange('qualityManagerCheck', event.target.value)} disabled={isReadOnly} placeholder={f('qualityManagerCheckPlaceholder')} />
              </Field>
            </div>
            <Field label={f('correctionReason')} htmlFor="vm-home-correctionReason">
              <TextArea id="vm-home-correctionReason" rows={2} value={ev.correctionReason || ''} onChange={(event) => onEvalChange('correctionReason', event.target.value)} disabled={isReadOnly} placeholder={f('correctionReasonPlaceholder')} />
            </Field>
          </div>
        </SectionCard>
      )}

      <SectionCard {...section('I')} tone="amber">
        <div className={GRID_3}>
          <Field label={f('dateInitialSourceReceived')} htmlFor={fieldAnchor('dateInitialSourceReceived')} {...req('dateInitialSourceReceived')}>
            {dateInput('dateInitialSourceReceived', ev.dateInitialSourceReceived, (value) => onEvalChange('dateInitialSourceReceived', value))}
          </Field>
          <Field label={f('dateLatestSourceRevision')} htmlFor={fieldAnchor('dateLatestSourceRevision')}>
            {dateInput('dateLatestSourceRevision', ev.dateLatestSourceRevision, (value) => onEvalChange('dateLatestSourceRevision', value))}
          </Field>
          <Field label={f('dateSourceFinalized')} htmlFor={fieldAnchor('dateSourceFinalized')} {...req('dateSourceFinalized')}>
            {dateInput('dateSourceFinalized', ev.dateSourceFinalized, (value) => onEvalChange('dateSourceFinalized', value))}
          </Field>
        </div>
      </SectionCard>

      <SectionCard {...section('J')} tone="emerald">
        <div className={FIELD_GRID}>
          <Field label={f('wasAeSaeTaken')} {...req('wasAeSaeTaken')}>
            <div id={fieldAnchor('wasAeSaeTaken')}>
              <YesNo label={f('wasAeSaeTaken')} value={ev.wasAeSaeTaken} onChange={(value) => onEvalChange('wasAeSaeTaken', value)} disabled={isReadOnly} />
            </div>
          </Field>
          <Field label={f('dcfCreated')} {...req('dcfCreated')}>
            <div id={fieldAnchor('dcfCreated')}>
              <YesNo label={f('dcfCreated')} value={ev.dcfCreated} onChange={(value) => onEvalChange('dcfCreated', value)} disabled={isReadOnly} />
            </div>
          </Field>
        </div>
      </SectionCard>
    </div>
  );
};

HomeEvaluation.propTypes = {
  formData: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  isAdmin: PropTypes.bool,
  missingKeys: PropTypes.instanceOf(Set).isRequired,
  sections: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onEvalChange: PropTypes.func.isRequired,
};

export default HomeEvaluation;
